/**
 * StrategyAudioPanel — age-aware companion audio bolt-on.
 *
 * Drops into any audio surface (Learn topic, Series episode, etc.). The user
 * picks their age range + earner tier and gets a 3-minute "how to use this
 * in real life" implementation narration from Leon's voice clone.
 *
 * Props:
 *   - sourceUrl: backend URL to hit (already includes /api). The component
 *     appends `?age=X&tier=t1..t5`.
 *   - testIdPrefix: unique data-testid prefix for QA hooks.
 */
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Spinner, Sparkle } from "@phosphor-icons/react";
import { api, API_BASE, auth } from "../lib/api";

const AGE_BUCKETS = [
  { id: "25-29", label: "25 – 29", stage: "Foundation",         age: 27 },
  { id: "30-34", label: "30 – 34", stage: "Early Acceleration", age: 32 },
  { id: "35-39", label: "35 – 39", stage: "Peak Acceleration",  age: 37 },
  { id: "40-49", label: "40 – 49", stage: "Peak Earning",       age: 44 },
  { id: "50-59", label: "50 – 59", stage: "Pre-Retirement",     age: 54 },
  { id: "60-69", label: "60 – 69", stage: "Transition",         age: 64 },
  { id: "70-79", label: "70 – 79", stage: "Legacy",             age: 74 },
];

const TIERS = [
  { id: "t1", label: "Working Wealth",  sub: "$40K – $150K" },
  { id: "t2", label: "Rising Pro",       sub: "$180K – $250K" },
  { id: "t3", label: "Established Pro",  sub: "$280K – $450K" },
  { id: "t4", label: "Executive Class",  sub: "$500K – $750K" },
  { id: "t5", label: "Capital Owner",    sub: "$780K+" },
];

const STORAGE_KEY = "exodus.strategy.profile";

// Migrate legacy stored values to the current schema. Returns null for
// values that no longer exist so the picker is empty rather than crashing.
const LEGACY_BUCKETS = { "30-39": "30-34" };           // old merged bucket
const LEGACY_TIERS = { common: "t1", high: "t4" };     // old 2-tier voice

function normalizeBucket(b) {
  if (!b) return null;
  if (LEGACY_BUCKETS[b]) return LEGACY_BUCKETS[b];
  return AGE_BUCKETS.some((x) => x.id === b) ? b : null;
}

function normalizeTier(t) {
  if (!t) return null;
  if (LEGACY_TIERS[t]) return LEGACY_TIERS[t];
  return TIERS.some((x) => x.id === t) ? t : null;
}

function loadSavedProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { bucket: null, tier: null };
    const p = JSON.parse(raw);
    return {
      bucket: normalizeBucket(p.bucket),
      tier: normalizeTier(p.tier),
    };
  } catch (_e) {
    return { bucket: null, tier: null };
  }
}

function saveProfile(bucket, tier) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ bucket, tier }));
  } catch (_e) {
    /* ignore */
  }
}

export default function StrategyAudioPanel({ sourcePath, testIdPrefix = "strategy" }) {
  const initial = loadSavedProfile();
  const [bucketId, setBucketId] = useState(initial.bucket);
  const [tierId, setTierId] = useState(initial.tier);
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);

  // Persist normalized values back on first mount (cleans up legacy
  // localStorage entries from before the 5-tier / 7-bucket rewrite).
  useEffect(() => {
    saveProfile(initial.bucket, initial.tier);
  }, []);

  // On mount, if user is logged in, hydrate from server (overrides localStorage)
  useEffect(() => {
    if (!auth.isLoggedIn()) return;
    api.get("/users/strategy-profile")
      .then((r) => {
        const p = r.data || {};
        const b = normalizeBucket(p.bucket);
        const t = normalizeTier(p.tier);
        if (b) setBucketId(b);
        if (t) setTierId(t);
        if (b || t) saveProfile(b, t);
      })
      .catch(() => {/* ignore — fall back to localStorage */});
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Reset audio whenever the profile changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setState("idle");
  }, [bucketId, tierId, sourcePath]);

  // Push selection to server when both are picked + user is logged in
  const persistRemote = (b, t) => {
    if (!auth.isLoggedIn() || !b || !t) return;
    api.post("/users/strategy-profile", { bucket: b, tier: t }).catch(() => {/* ignore */});
  };

  const pickBucket = (id) => {
    setBucketId(id);
    saveProfile(id, tierId);
    persistRemote(id, tierId);
  };
  const pickTier = (id) => {
    setTierId(id);
    saveProfile(bucketId, id);
    persistRemote(bucketId, id);
  };

  const toggleAudio = () => {
    if (!bucketId || !tierId) return;
    if (state === "playing" && audioRef.current) {
      audioRef.current.pause();
      setState("paused");
      return;
    }
    if (state === "paused" && audioRef.current) {
      audioRef.current.play().then(() => setState("playing")).catch(() => setState("idle"));
      return;
    }
    setState("loading");
    const bucket = AGE_BUCKETS.find((b) => b.id === bucketId);
    if (!bucket) { setState("idle"); return; }
    const url = `${API_BASE}${sourcePath}?age=${bucket.age}&tier=${tierId}`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onplaying = () => setState("playing");
    audio.onpause = () => setState((s) => (s === "playing" ? "paused" : s));
    audio.onended = () => setState("idle");
    audio.onerror = () => setState("idle");
    audio.play().catch(() => setState("idle"));
  };

  const bucket = AGE_BUCKETS.find((b) => b.id === bucketId);
  const tier = TIERS.find((t) => t.id === tierId);
  const ready = !!bucket && !!tier;

  return (
    <section
      data-testid={`${testIdPrefix}-panel`}
      className="mt-12 border border-exodus-gold/30 bg-exodus-elevated/40 p-6 lg:p-8"
    >
      <div className="flex items-center gap-3">
        <Sparkle size={20} weight="thin" className="text-exodus-gold" />
        <p className="eyebrow text-exodus-gold">Strategy · How to use this in real life</p>
      </div>
      <h3 className="font-display text-2xl lg:text-3xl mt-3 leading-tight">
        Pick your age range. Pick your tier. Get the 3-minute implementation.
      </h3>
      <p className="text-sm text-exodus-muted mt-3 max-w-2xl leading-relaxed">
        A real walk-through with the jobs, paychecks, and expenses you actually
        live with. How to take this concept and put it to work the next 90 days.
      </p>

      {/* AGE GRID */}
      <div className="mt-7">
        <p className="eyebrow text-xs mb-3">Age range</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
          {AGE_BUCKETS.map((b) => {
            const active = b.id === bucketId;
            return (
              <button
                key={b.id}
                onClick={() => pickBucket(b.id)}
                data-testid={`${testIdPrefix}-age-${b.id}`}
                className={`px-3 py-3 text-left transition-all border ${
                  active
                    ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
                    : "border-exodus-border text-exodus-text hover:border-exodus-gold/60"
                }`}
              >
                <p className="font-display text-lg leading-none">{b.label}</p>
                <p className={`text-[10px] mt-1 uppercase tracking-wide ${active ? "text-exodus-bg/70" : "text-exodus-muted"}`}>
                  {b.stage}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* TIER PICK */}
      <div className="mt-6">
        <p className="eyebrow text-xs mb-3">Earner tier</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {TIERS.map((t) => {
            const active = t.id === tierId;
            return (
              <button
                key={t.id}
                onClick={() => pickTier(t.id)}
                data-testid={`${testIdPrefix}-tier-${t.id}`}
                className={`px-4 py-3 text-left transition-all border ${
                  active
                    ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
                    : "border-exodus-border text-exodus-text hover:border-exodus-gold/60"
                }`}
              >
                <p className="font-display text-base">{t.label}</p>
                <p className={`text-xs mt-1 ${active ? "text-exodus-bg/70" : "text-exodus-muted"}`}>
                  {t.sub}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* PLAY BUTTON */}
      <div className="mt-7 flex items-center gap-4 flex-wrap">
        <button
          data-testid={`${testIdPrefix}-play-btn`}
          disabled={!ready}
          onClick={toggleAudio}
          className={`inline-flex items-center gap-3 px-5 py-3 border transition-all ${
            !ready
              ? "border-exodus-border text-exodus-muted cursor-not-allowed"
              : state === "playing"
                ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
                : "border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg"
          }`}
        >
          {state === "loading" ? (
            <Spinner size={18} className="animate-spin" />
          ) : state === "playing" ? (
            <Pause size={18} weight="fill" />
          ) : (
            <Play size={18} weight="fill" />
          )}
          <span className="text-xs eyebrow tracking-[3px]">
            {!ready
              ? "Pick age + tier"
              : state === "loading"
                ? "Generating your 3-min playbook…"
                : state === "playing"
                  ? "Pause"
                  : state === "paused"
                    ? "Resume"
                    : "Play 3-min Strategy"}
          </span>
        </button>
        {ready && (
          <p className="text-xs text-exodus-muted">
            For <span className="text-exodus-text">{bucket.label}</span> · <span className="text-exodus-text">{tier.label}</span>
          </p>
        )}
      </div>
      {state === "loading" && (
        <p className="text-[11px] text-exodus-muted mt-3 italic">
          First listen takes 30-45 seconds while we write a unique script for your profile.
          Replays are instant from cache.
        </p>
      )}
    </section>
  );
}
